// FileUploadWizard.jsx
import React, { useState } from "react";
import {
  Box,
  Flex,
  Icon,
  Input,
  Link,
  Progress,
  Step,
  StepIndicator,
  StepSeparator,
  StepStatus,
  Stepper,
  StepTitle,
  Text,
  useToast,
  VStack,
  Button,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { FiUpload, FiCheck, FiSave } from "react-icons/fi";
import * as XLSX from "xlsx";
import axiosInstance from "../../../helpers/axiosInstance";
import ColumnMapper from "./ColumnMapper";

const FileUploadWizard = ({
  selectedProgramId,
  fileUniqueId,
  handleUploadFileCustom, // (file) => Promise
  handleFileUpload, // e => parent sets its own file state (optional)
  handleDragOver, // parent’s global drag‑over (optional)
  isUploadLoading = false, // parent spinner while uploading
  campaignType,
  currentClientAnswers, setCurrentClientAnswers
}) => {
  const toast = useToast();

  /* step progress */
  const [step, setStep] = useState(0);

  /* local file + header parsing */
  const [file, setFile] = useState(null);
  const [fileColumns, setFileColumns] = useState([]);
  const [loadingHeaders, setLoadingHeaders] = useState(false);

  /* mapping */
  const [defaults, setDefaults] = useState([]); // [{ name, required }]
  const [mapping, setMapping] = useState({});

  /* extra spinner while we await handleUploadFileCustom */
  const [uploading, setUploading] = useState(false);



  const autoMap = (def, cols) =>
    Object.fromEntries(
      def.map(({ name }) => {
        const exact = cols.find((c) => c.toLowerCase() === name.toLowerCase());
        return [name, exact || ""];
      })
    );

  /* ---- Stage 0 : local selection -> build mapping UI ---- */
  // inside FileUploadWizard.jsx
  // — FileUploadWizard.jsx —
  const buildMappingStep = async (rawFile) => {
    try {
      setLoadingHeaders(true);

      /* 1️⃣ POST the file so the server extracts headers */
      const form = new FormData();
      form.append("file", rawFile); // must be 'file'

      const res = await axiosInstance.post(
        `/program/get-file-headers/${selectedProgramId}/${fileUniqueId}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      console.log("res", res);
      /* 2️⃣ unpack the server response */
      const {
        file_headers = [],
        required_input_headers = [],
        required_output_headers = [],
      } = res.data?.data || {};

      if (!required_input_headers.length && !required_output_headers.length) {
        throw new Error("Server did not return header definitions.");
      }

      /* 3️⃣ build default‑column list  (tag each row by kind) */
      const inputDefs = required_input_headers.map((h) => ({
        name: h,
        required: h !== "prospect_linkedin_about_section", // ← only others stay required
        kind: "input",
      }));
      const outputDefs = required_output_headers.map((h) => ({
        name: h,
        required: true,
        kind: "output",
      }));
      const defs = [...inputDefs, ...outputDefs];

      /* 4️⃣ update local state and auto‑map identical names */
      setFile(rawFile);
      setFileColumns(file_headers);
      setDefaults(defs);
      setMapping(autoMap(defs, file_headers));

      setStep(1); // proceed to “Map Columns”
    } catch (err) {
      toast({
        title: "File error",
        description: err.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setLoadingHeaders(false);
    }
  };

  // anywhere above your render…
  const questionKeys = fileColumns.filter((h) =>
    h.startsWith("current_client_")
  );

  /**  drop‑zone & file‑input wrappers  **/
  const onFileInputChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    /* let parent know (keeps parity with old flow) */
    handleFileUpload?.(e);

    /* then build header UI */
    buildMappingStep(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) buildMappingStep(f);
  };

  /* ---- Stage 1 : confirm mapping + upload ---- */
  const handleSaveAndUpload = async () => {
    /* 1️⃣  validate that all required columns are mapped */
    const missing = defaults.filter((d) => d.required && !mapping[d.name]);
    if (missing.length) {
      toast({
        title: "Missing required mappings",
        description: "Please map every required field before continuing.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    /* 2️⃣  build the structure the backend expects */
    const input_mapping = {};
    const output_mapping = {};

    defaults.forEach(({ name, kind }) => {
      if (kind === "input") input_mapping[name] = mapping[name] || "";
      if (kind === "output") output_mapping[name] = mapping[name] || "";
    });

    const mapping_headers = [{ input_mapping, output_mapping }];

    /* 3️⃣  upload */
    try {
      setUploading(true);
      await handleUploadFileCustom(file, mapping_headers); // <-- pass both params
      // wizard can close or reset here if you like
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err.message || "Unable to upload file.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  };

  /* ---- RENDER ---- */
  return (
    <Box>
      {/* ---------- tiny stepper ---------- */}
      <Stepper size="sm" index={step} mb={4}>
        {["Upload", "Map Columns"].map((label) => (
          <Step key={label}>
            <StepIndicator>
              <StepStatus
                complete={<FiCheck />}
                active={<FiUpload />}
                inactive={<FiUpload />}
              />
            </StepIndicator>
            <Box flexShrink={0}>
              <StepTitle mb={0}>{label}</StepTitle>
            </Box>
            <StepSeparator />
          </Step>
        ))}
      </Stepper>

      {/* ---------- STEP 0 ---------- */}
      {step === 0 && (
        <Flex
          h="45vh"
          align="center"
          justify="center"
          bg="gray.50"
          border="2px dashed"
          borderColor="gray.300"
          borderRadius="lg"
          onDrop={onDrop}
          onDragOver={(e) => {
            e.preventDefault();
            handleDragOver?.(e);
          }}
          cursor="pointer"
          position="relative"
          _hover={{ bg: "gray.100" }}
          onClick={() => document.getElementById("upl")?.click()}
        >
          <VStack spacing={2}>
            <Icon as={FiUpload} boxSize={12} color="gray.400" />
            <Text fontWeight="bold" mb={2}>
              Drop the files here or <Link color="#3C9AFF">choose a file</Link>
            </Text>
            <Text fontSize="sm" color="gray.500">
              .xlsx, csv
            </Text>
            <Input
              id="upl"
              type="file"
              opacity={0}
              inset={0}
              position="absolute"
              cursor="pointer"
              onChange={onFileInputChange}
            />

            {(loadingHeaders || isUploadLoading) && (
              <Progress size="xs" isIndeterminate w="full" mt={2} />
            )}
            {file && !loadingHeaders && (
              <Text fontSize="sm" mt={2}>
                {file.name}
              </Text>
            )}
          </VStack>
        </Flex>
      )}

      {/* ---------- STEP 1 ---------- */}
      {step === 1 && (
  
          <ColumnMapper
            defaults={defaults}
            fileColumns={fileColumns}
            mapping={mapping}
            setMapping={setMapping}
            onClick={handleSaveAndUpload}
            setStep={setStep}
            campaignType={campaignType}
            questionKeys={questionKeys}
            currentClientAnswers={currentClientAnswers}
             setCurrentClientAnswers={setCurrentClientAnswers}
          />
         
     
      )}
    </Box>
  );
};

export default FileUploadWizard;
